import { CurrencyCode } from 'further-types/lp/vehicle';
import { InvestorAllocationTotals } from '../../types';
import Table, { Column as TableColumn } from 'further-ui/components/Table';
import AvatarWithName from 'components/new/AvatarWithName';
import {
  numberToCurrencyString,
  numberToPercentageString,
  pluralize,
} from 'further-ui/utils';
import { sumBy } from 'lodash';

type Props = {
  currency: CurrencyCode;
  investorAllocationsTotals: Array<InvestorAllocationTotals>;
};

const PerInvestorAllocationTable: React.FC<Props> = ({
  currency,
  investorAllocationsTotals,
}) => {
  const rows = investorAllocationsTotals.map((investorAllocation) => ({
    id: investorAllocation.commitmentId,
    ...investorAllocation,
  }));

  const totals = {
    receivedConsideration: sumBy(rows, 'receivedConsideration'),
    escrowConsideration: sumBy(rows, 'escrowConsideration'),
    earnoutConsideration: sumBy(rows, 'earnoutConsideration'),
    withholdingTaxPayable: sumBy(rows, 'withholdingTaxPayable'),
    total: sumBy(rows, 'total'),
  };

  const columns: Array<TableColumn<(typeof rows)[number]>> = [
    {
      key: 'investorName',
      label: 'Investor',
      width: 200,
      render: ({ investorName }) => <AvatarWithName name={investorName} />,
      footer: `Total from ${rows.length} ${pluralize('investor', rows.length)}`,
    },
    {
      key: 'averageAllocationPercentage',
      label: 'Allocation (%)',
      width: 130,
      render: ({ averageAllocationPercentage }) =>
        numberToPercentageString(averageAllocationPercentage, {
          accuracy: 2,
          forceDecimals: true,
          noMultiply: true,
        }),
    },
    {
      key: 'receivedConsideration',
      label: 'Received consideration',
      width: 180,
      render: ({ receivedConsideration }) =>
        numberToCurrencyString(receivedConsideration, { currency }),
      footer: numberToCurrencyString(totals.receivedConsideration, {
        currency,
      }),
    },
    {
      key: 'escrowConsideration',
      label: 'Escrow consideration',
      width: 180,
      render: ({ escrowConsideration }) =>
        numberToCurrencyString(escrowConsideration, { currency }),
      footer: numberToCurrencyString(totals.escrowConsideration, { currency }),
    },
    {
      key: 'earnoutConsideration',
      label: 'Earnout consideration',
      width: 180,
      render: ({ earnoutConsideration }) =>
        numberToCurrencyString(earnoutConsideration, { currency }),
      footer: numberToCurrencyString(totals.earnoutConsideration, { currency }),
    },
    {
      key: 'withholdingTaxPayable',
      label: 'Withholding tax payable',
      width: 190,
      render: ({ withholdingTaxPayable }) =>
        numberToCurrencyString(withholdingTaxPayable, { currency }),
      footer: numberToCurrencyString(totals.withholdingTaxPayable, {
        currency,
      }),
    },
    {
      key: 'total',
      label: 'Total consideration',
      width: 170,
      render: ({ total }) => numberToCurrencyString(total, { currency }),
      footer: numberToCurrencyString(totals.total, { currency }),
    },
  ];

  return <Table scrollable rows={rows} columns={columns} />;
};

export default PerInvestorAllocationTable;
